import type {
  WhitebitDepthResponse,
  WhitebitMarketsResponse,
  WhitebitTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://whitebit.com';

/**
 * WhiteBIT REST API endpoints.
 * @see https://docs.whitebit.com/public/http-v4/
 */
export const whitebitRestEndpoints = {
  ticker: `${BASE_URL}/api/v4/public/ticker`,
  tickerV1: `${BASE_URL}/api/v1/public/ticker`,
  markets: `${BASE_URL}/api/v4/public/markets`,
  orderbook: (market: string) => `${BASE_URL}/api/v4/public/orderbook/${market}`,
  depth: (market: string) => `${BASE_URL}/api/v4/public/depth/${market}`,
  trades: (market: string) => `${BASE_URL}/api/v4/public/trades/${market}`,
  kline: `${BASE_URL}/api/v1/public/kline`,
} as const;

export interface WhitebitTickerResponse {
  [symbol: string]: {
    /** Last traded price */
    last_price: string;
    /** 24h high price */
    high: string; // Keeping typical interface even if v4 misses it for potential future or other endpoints
    /** 24h low price */
    low: string; // Keeping typical interface even if v4 misses it
    /** 24h price change percentage */
    change: string;
    /** 24h base volume */
    base_volume: string;
    /** 24h quote volume */
    quote_volume: string;
    [key: string]: unknown;
  };
}

export interface WhitebitTickerV1Response {
  success: boolean;
  message: string | null;
  result: {
    open: string;
    bid: string;
    ask: string;
    low: string;
    high: string;
    last: string;
    volume: string;
    deal: string;
    change: string;
  };
}

export interface WhitebitTickerV1Request {
  market: string;
}

export interface WhitebitDepthRequest {
  /** Number of levels (default: 100, max: 100) */
  limit?: number;
}

export interface WhitebitTradesRequest {
  /** Trade type filter: "buy" | "sell" (optional) */
  type?: 'buy' | 'sell';
}

/**
 * WhiteBIT kline intervals (V1 API format).
 * @see https://docs.whitebit.com/public/http-v1/#kline
 */
export type WhitebitKlineRestInterval =
  | '1m'
  | '3m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '6h'
  | '8h'
  | '12h'
  | '1d'
  | '3d'
  | '1w'
  | '1M';

export interface WhitebitKlineRequest {
  /** Trading pair (e.g., "BTC_USDT") */
  market: string;
  /** Start time in SECONDS */
  start?: number;
  /** End time in SECONDS */
  end?: number;
  /** Kline interval. Default: 1h */
  interval?: WhitebitKlineRestInterval;
  /** Limit of results (1-1440). Default: 1440 */
  limit?: number;
}

/**
 * WhiteBIT kline response from V1 API.
 * Each kline is an array: [time, open, close, high, low, volumeStock, volumeMoney]
 * IMPORTANT: Order is [time, open, close, high, low, ...] not typical [time, open, high, low, close, ...]
 */
export interface WhitebitKlineResponse {
  success: boolean;
  message: string | null;
  result: WhitebitKline[];
}

/**
 * WhiteBIT kline tuple: [time, open, close, high, low, volumeStock, volumeMoney]
 * Note: WhiteBIT uses [O, C, H, L] order instead of typical [O, H, L, C]
 */
export type WhitebitKline = [
  number, // time in SECONDS
  string, // open
  string, // close
  string, // high
  string, // low
  string, // volumeStock (base volume)
  string, // volumeMoney (quote volume)
];

/**
 * Fetches 24-hour ticker statistics for all symbols or a specific symbol.
 * Note: This endpoint returns data for all trading pairs. Filter by symbol key.
 * @see https://docs.whitebit.com/public/http-v4/#market-activity
 */
export const getWhitebitTicker = async (
  options?: RestRequestOptions,
): Promise<WhitebitTickerResponse> => {
  const response = await get<WhitebitTickerResponse>({
    url: whitebitRestEndpoints.ticker,
    options,
  });

  return response.data;
};

/**
 * Fetches single market ticker data including high/low stats (V1 API).
 * @see https://docs.whitebit.com/public/http-v1/#ticker
 */
export const getWhitebitTickerV1 = async (
  params: WhitebitTickerV1Request,
  options?: RestRequestOptions,
): Promise<WhitebitTickerV1Response> => {
  const response = await get<WhitebitTickerV1Response, WhitebitTickerV1Request>({
    url: whitebitRestEndpoints.tickerV1,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches all available trading pairs/markets.
 * @see https://docs.whitebit.com/public/http-v4/#market-info
 */
export const getWhitebitMarkets = async (
  options?: RestRequestOptions,
): Promise<WhitebitMarketsResponse> => {
  const response = await get<WhitebitMarketsResponse>({
    url: whitebitRestEndpoints.markets,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth for a specific market.
 * @param market Trading pair (e.g., "BTC_USDT")
 * @param params Optional parameters (limit)
 * @see https://docs.whitebit.com/public/http-v4/#orderbook
 */
export const getWhitebitDepth = async (
  market: string,
  params?: WhitebitDepthRequest,
  options?: RestRequestOptions,
): Promise<WhitebitDepthResponse> => {
  const response = await get<WhitebitDepthResponse, WhitebitDepthRequest>({
    url: whitebitRestEndpoints.orderbook(market),
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches recent trades for a specific market.
 * @param market Trading pair (e.g., "BTC_USDT")
 * @param params Optional parameters (type filter)
 * @see https://docs.whitebit.com/public/http-v4/#recent-trades
 */
export const getWhitebitTrades = async (
  market: string,
  params?: WhitebitTradesRequest,
  options?: RestRequestOptions,
): Promise<WhitebitTradesResponse> => {
  const response = await get<WhitebitTradesResponse, WhitebitTradesRequest>({
    url: whitebitRestEndpoints.trades(market),
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches kline (candlestick) data for a specific market.
 * IMPORTANT: Timestamps in request and response are in SECONDS, not milliseconds.
 * IMPORTANT: Response format is [time, open, close, high, low, ...] not [time, open, high, low, close, ...]
 *
 * @param params Kline request parameters
 * @see https://docs.whitebit.com/public/http-v1/#kline
 */
export const getWhitebitKlines = async (
  params: WhitebitKlineRequest,
  options?: RestRequestOptions,
): Promise<WhitebitKlineResponse> => {
  const response = await get<WhitebitKlineResponse, WhitebitKlineRequest>({
    url: whitebitRestEndpoints.kline,
    params,
    options,
  });

  return response.data;
};
